import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { loginSchema, type LoginFormValues } from '@/modules/auth/schemas/auth.schemas';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { getLoginNavigationTarget } from '@/modules/auth/lib/loginFlow';
import { getProductBrandingText } from '@/modules/auth/config/productBranding';
import { useProductAuthBrandingContext } from '@/modules/auth/context/ProductAuthBrandingContext';
import { isApiError } from '@/shared/lib/apiClient';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { PasswordInput } from '@/shared/components/ui/password-input';
import { Label } from '@/shared/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';

const REMEMBER_EMAIL_KEY = 'auth-remember-email';

const loginSearchSchema = z.object({
  return: z.string().optional(),
  product: z.string().optional(),
  product_slug: z.string().optional(),
  locale: z.enum(['ar', 'en']).optional(),
  checkout_session: z.string().optional(),
});

export const Route = createFileRoute('/auth/login')({
  validateSearch: (search) => loginSearchSchema.parse(search),
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: '/auth/login' });
  const productBranding = useProductAuthBrandingContext();
  const [rememberMe, setRememberMe] = useState(false);

  const brandedCopy = productBranding
    ? getProductBrandingText(productBranding.branding, productBranding.locale)
    : null;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    setValue,
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  useEffect(() => {
    const savedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY);
    if (savedEmail) {
      setValue('email', savedEmail);
      setRememberMe(true);
    }
  }, [setValue]);

  const onSubmit = async (values: LoginFormValues) => {
    try {
      if (rememberMe) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, values.email);
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }

      const result = await login(values.email, values.password);

      if (search.return) {
        window.location.assign(search.return);
        return;
      }

      await navigate({ to: getLoginNavigationTarget(result) });
    } catch (err) {
      let message = t('auth.errors.generic');
      if (isApiError(err)) {
        if (err.status === 401 && err.code === 'EMAIL_NOT_VERIFIED') {
          message = t('auth.errors.emailNotVerified');
        } else if (err.status === 403 && err.code === 'MFA_SETUP_REQUIRED') {
          message = t('auth.errors.mfaSetupRequired');
        } else if (err.status === 401) {
          message = t('auth.errors.invalidCredentials');
        } else if (err.status === 423) {
          message = t('auth.errors.accountLocked');
        } else if (err.status === 429) {
          message = t('auth.errors.tooManyRequests', {
            seconds: err.retryAfterSeconds ?? 60,
          });
        }
      }
      setError('root', { message });
    }
  };

  const title = brandedCopy?.title ?? t('auth.login.title');
  const subtitle = brandedCopy?.subtitle ?? t('auth.login.subtitle');
  const cardTitle = brandedCopy?.cardTitle ?? title;
  const cardSubtitle = brandedCopy?.cardSubtitle ?? subtitle;
  const submitLabel = brandedCopy?.submit ?? t('auth.login.submit');
  const submittingLabel = brandedCopy?.submitting ?? t('auth.login.submitting');
  const rememberMeLabel = brandedCopy?.rememberMe ?? t('auth.login.rememberMe');

  return (
    <Card className={cn(productBranding && 'border-0 bg-transparent shadow-none')}>
      <CardHeader className={cn(productBranding ? 'space-y-1.5 px-8 pb-2 pt-8' : undefined)}>
        {!productBranding && (
          <>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{subtitle}</CardDescription>
          </>
        )}
        {productBranding && (
          <div className="hidden lg:block space-y-1.5">
            <CardTitle className="branded-card-title">{cardTitle}</CardTitle>
            <CardDescription className="branded-card-subtitle">{cardSubtitle}</CardDescription>
          </div>
        )}
      </CardHeader>
      <CardContent className={cn(productBranding ? 'px-8 pb-8 pt-2' : undefined)}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className={cn('space-y-4', productBranding && 'space-y-5')}
        >
          {errors.root && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className={cn(productBranding && 'branded-field-label')}>
              {t('auth.login.email')}
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder={t('auth.login.emailPlaceholder')}
              aria-invalid={!!errors.email}
              className={cn(productBranding && 'branded-input')}
              {...register('email')}
            />
            {errors.email && (
              <p className="text-xs text-destructive">{t(errors.email.message ?? '')}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className={cn(productBranding && 'branded-field-label')}>
              {t('auth.login.password')}
            </Label>
            <PasswordInput
              id="password"
              autoComplete="current-password"
              placeholder={t('auth.login.passwordPlaceholder')}
              aria-invalid={!!errors.password}
              className={cn(productBranding && 'branded-input')}
              toggleClassName={cn(productBranding && 'branded-password-toggle')}
              {...register('password')}
            />
            {errors.password && (
              <p className="text-xs text-destructive">{t(errors.password.message ?? '')}</p>
            )}
          </div>

          <label className="flex cursor-pointer items-center gap-2.5 pt-0.5">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(event) => setRememberMe(event.target.checked)}
              className={cn(
                productBranding
                  ? 'branded-checkbox'
                  : 'h-4 w-4 rounded border-border text-primary focus:ring-primary',
              )}
            />
            <span
              className={cn(productBranding ? 'branded-remember-label' : 'text-sm text-foreground')}
            >
              {rememberMeLabel}
            </span>
          </label>

          <Button
            type="submit"
            variant={productBranding ? 'ghost' : 'default'}
            className={cn('w-full', productBranding && 'branded-submit')}
            isLoading={isSubmitting}
          >
            {isSubmitting ? submittingLabel : submitLabel}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
